import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Send, Check, Loader2, RotateCcw } from "lucide-react";
import { toast } from "sonner";

const fmtDate = (iso) => {
    if (!iso) return "—";
    const [y, m, d] = iso.split("-");
    return `${d}/${m}/${y}`;
};

const EVENT_COLORS = {
    assunzione: "bg-emerald-100 text-emerald-800",
    cessazione: "bg-amber-100 text-amber-800",
    proroga: "bg-blue-100 text-blue-800",
    trasformazione: "bg-violet-100 text-violet-800",
};

/** Card shown on the Scadenze page: pending UNILAV mandatory communications. */
export const UnilavRemindersCard = () => {
    const [reminders, setReminders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showDone, setShowDone] = useState(false);
    const [busyId, setBusyId] = useState(null);

    const fetchReminders = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/unilav/reminders");
            setReminders(data);
        } catch {
            // silent — the card simply stays empty
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchReminders();
    }, []);

    const setStatus = async (r, status) => {
        setBusyId(r.id);
        try {
            await api.put(`/unilav/reminders/${r.id}`, { status });
            toast.success(status === "done" ? "UNILAV segnato come inviato" : "Ripristinato come da inviare");
            fetchReminders();
        } catch {
            toast.error("Errore nell'aggiornamento");
        } finally {
            setBusyId(null);
        }
    };

    const pending = reminders.filter((r) => r.status === "pending");
    const done = reminders.filter((r) => r.status === "done");
    const today = new Date().toISOString().slice(0, 10);

    if (loading || (pending.length === 0 && done.length === 0)) return null;

    return (
        <div className="mb-6 rounded-lg border border-slate-200 bg-white p-5 shadow-sm" data-testid="unilav-reminders-card">
            <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                <h2 className="flex items-center gap-2 font-display text-base font-semibold text-[#376DB3]">
                    <Send className="h-4 w-4" />
                    Comunicazioni UNILAV
                    {pending.length > 0 && (
                        <Badge className="bg-red-600 text-white" data-testid="unilav-pending-count">{pending.length} da inviare</Badge>
                    )}
                </h2>
                {done.length > 0 && (
                    <button
                        type="button"
                        onClick={() => setShowDone((v) => !v)}
                        className="text-xs text-slate-500 underline-offset-2 hover:underline"
                        data-testid="unilav-toggle-done"
                    >
                        {showDone ? "Nascondi inviate" : `Mostra inviate (${done.length})`}
                    </button>
                )}
            </div>

            {pending.length === 0 && (
                <p className="text-sm text-slate-500" data-testid="unilav-all-sent">Nessuna comunicazione in sospeso. ✓</p>
            )}

            <div className="space-y-2">
                {[...pending, ...(showDone ? done : [])].map((r) => {
                    const overdue = r.status === "pending" && r.due_date < today;
                    return (
                        <div
                            key={r.id}
                            className={`flex flex-wrap items-center justify-between gap-2 rounded-md border px-3 py-2 ${
                                r.status === "done"
                                    ? "border-slate-100 bg-slate-50 opacity-60"
                                    : overdue
                                    ? "border-red-200 bg-red-50"
                                    : "border-slate-200"
                            }`}
                            data-testid={`unilav-reminder-${r.id}`}
                        >
                            <div className="flex min-w-0 flex-wrap items-center gap-2 text-sm">
                                <Badge className={`${EVENT_COLORS[r.event_type] || "bg-slate-100 text-slate-700"} capitalize`}>
                                    {r.event_label}
                                </Badge>
                                <Link to={`/dipendenti/${r.employee_id}`} className="font-medium text-slate-800 hover:text-[#376DB3]">
                                    {r.employee_name}
                                </Link>
                                <span className="text-xs text-slate-500">evento: {fmtDate(r.event_date)}</span>
                                <span className={`text-xs font-medium ${overdue ? "text-red-700" : "text-slate-500"}`}>
                                    {overdue ? "⚠ scaduto il" : "invia entro"} {fmtDate(r.due_date)}
                                </span>
                            </div>
                            {r.status === "pending" ? (
                                <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => setStatus(r, "done")}
                                    disabled={busyId === r.id}
                                    className="gap-1.5 border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                                    data-testid={`unilav-done-${r.id}`}
                                >
                                    {busyId === r.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
                                    Segna inviato
                                </Button>
                            ) : (
                                <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => setStatus(r, "pending")}
                                    disabled={busyId === r.id}
                                    className="gap-1.5 text-slate-500"
                                    data-testid={`unilav-undo-${r.id}`}
                                >
                                    <RotateCcw className="h-3.5 w-3.5" /> Ripristina
                                </Button>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};
